import {
  FloatInputContainer,
  ProductContractSelection,
} from 'common/containers';
import React, { useCallback, useMemo, useState } from 'react';
import { Header, Form, Segment } from 'semantic-ui-react';
import Datetime from 'react-datetime';
import moment, { Moment } from 'moment';
import {
  EOD_SETTLEMENT_TYPE,
  INTRADAY_SETTLEMENT_TYPE,
  SETTLEMENT_TYPE_OPTIONS,
} from 'constants/settlementTypes';
import isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import { createCdfUnitFundingAmountPromise } from 'reducers/cdfUnitFundingAmountReducer';
import { useAppSelector } from 'hooks/useAppSelector';
import { createLoadingSelector } from 'erisxkit/client';
import { roles } from 'constants/userRoles';
import { getUserHasAnyRole } from 'reducers/usersReducer';

export const FUNDING_RATES_READ_ONLY_ROLES = [roles.RISK];

const SETTLEMENT_TYPE_NAME = 'creationType';

type CreateFundingRateArgs = {
  tradeDate: string;
  contractSymbol: string;
  creationType: string;
  fundingRate: number;
};

const isValidFundingRate = ({
  tradeDate,
  contractSymbol,
  creationType,
  fundingRate,
}: Partial<CreateFundingRateArgs>) =>
  !isEmpty(tradeDate) &&
  !isEmpty(contractSymbol) &&
  !isEmpty(creationType) &&
  !isEmpty(fundingRate);

export const SUBMIT_BTN_ID = 'submit-funding-rate';

type Props = {
  onCreate: () => void;
};
const AddFundingRate = ({ onCreate }: Props) => {
  // Values for input submission (top component)
  const [values, setValues] = useState({
    [SETTLEMENT_TYPE_NAME]: EOD_SETTLEMENT_TYPE,
  });

  const loading = useAppSelector(
    createLoadingSelector(['CREATE_CDF_UNIT_FUNDING_AMOUNT']),
  );

  const dispatch = useDispatch();
  const handleChange = useCallback(
    ({ name, value }: { name: string; value: unknown }) => {
      setValues({
        ...values,
        [name]: value,
      });
    },
    [values],
  );

  const handleSubmit = useCallback(() => {
    createCdfUnitFundingAmountPromise(values, dispatch).then(() => {
      onCreate();
    });
  }, [dispatch, onCreate, values]);

  const isValid = useMemo(() => isValidFundingRate(values), [values]);

  const settlementTypeOptions = useMemo(
    () =>
      SETTLEMENT_TYPE_OPTIONS.filter(
        (opt) =>
          opt?.value === EOD_SETTLEMENT_TYPE ||
          opt?.value === INTRADAY_SETTLEMENT_TYPE,
      ),
    [],
  );

  const isReadOnlyRole = useAppSelector((state) =>
    getUserHasAnyRole(state, FUNDING_RATES_READ_ONLY_ROLES),
  );

  return (
    <Segment>
      <Header as="h2" icon textAlign="left">
        Add Funding Rate
      </Header>
      <Form onSubmit={handleSubmit}>
        <Form.Group style={{ flexWrap: 'wrap' }}>
          <Form.Field
            control={Datetime}
            label="Date"
            inputProps={{ placeholder: 'MM/DD/YYYY', name: 'date' }}
            timeFormat={false}
            name="tradeDate"
            closeOnSelect
            onChange={(currentDate: Moment) => {
              handleChange({
                name: 'tradeDate',
                value: moment(currentDate).format('YYYY-MM-DD'),
              });
            }}
          />
          <ProductContractSelection
            contractName="contractSymbol"
            onChange={(_, { name, value }) => {
              handleChange({ name, value });
            }}
            required
            showExchangeDropdown
          />
          <FloatInputContainer
            required
            label="Funding Rate"
            name="fundingRate"
            id="price"
            placeholder="0.00"
            onChange={(_, { value, name }) => {
              handleChange({ name, value });
            }}
          />
          <Form.Field
            required
            control={Form.Select}
            placeholder="Select a type"
            options={settlementTypeOptions}
            defaultValue={EOD_SETTLEMENT_TYPE}
            label="Settlement Type"
            name="settlementPriceType"
            id="settlementPriceType"
            onChange={(e, { value }) => {
              handleChange({ name: SETTLEMENT_TYPE_NAME, value });
            }}
          />
        </Form.Group>
        <div>
          <Form.Button
            disabled={!isValid || loading || isReadOnlyRole}
            id={SUBMIT_BTN_ID}
            data-testid={SUBMIT_BTN_ID}
            content="Add Funding Price"
            style={{ height: '38px' }}
            loading={loading}
          />
        </div>
      </Form>
    </Segment>
  );
};

export default AddFundingRate;
